import { useEffect, useMemo, useState, type FormEvent } from "react";
import { getOutlookCandidates, importOutlookEvent } from "./api";
import { AUTH_REQUIRED_MESSAGE } from "./auth";
import {
  candidateDates,
  candidateHiddenReason,
  filterCandidates,
  isFilterActive,
  parseMinDurationHours,
  type CandidateFilter,
} from "./outlook-import";
import type { Member, OutlookCandidate } from "./types";

interface Props {
  members: Member[];
  initialMemberId?: string;
  initialFrom: string;
  initialTo: string;
  membersError: string | null;
  membersLoading: boolean;
  signingIn: boolean;
  onSignIn: () => void;
}

function eventTime(event: OutlookCandidate): string {
  if (event.allDay) {
    return event.start === event.end ? `${event.start}（終日）` : `${event.start}〜${event.end}（終日）`;
  }
  return `${event.start.replace("T", " ")}〜${event.end.replace("T", " ")}`;
}

export default function OutlookImportPage({
  members,
  initialMemberId,
  initialFrom,
  initialTo,
  membersError,
  membersLoading,
  signingIn,
  onSignIn,
}: Props) {
  const [memberId, setMemberId] = useState(initialMemberId ?? "");
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [events, setEvents] = useState<OutlookCandidate[]>([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [participantsByDate, setParticipantsByDate] = useState<Record<string, string[]>>({});
  // 絞り込み条件はこの画面だけの作業状態として持つ（URL ハッシュには保存しない）。
  // 最小時間は入力途中（空欄・"1."・"25"）を壊さないよう文字列のまま持ち、使うときに数値へ変換する。
  const [teamAddress, setTeamAddress] = useState("");
  const [minDurationText, setMinDurationText] = useState("");
  const [excludeRecurring, setExcludeRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!members.some((member) => member.id === memberId)) {
      setMemberId(
        members.find((member) => member.id === initialMemberId)?.id ?? members[0]?.id ?? "",
      );
    }
  }, [initialMemberId, memberId, members]);

  const teamAddresses = useMemo(
    () => [...new Set(events.flatMap((event) => event.teamAddresses))].sort(),
    [events],
  );
  // 選んでいたチームアドレスが今の候補に無いときは、状態は変えずに「すべて」として扱う
  // （再検索や登録で候補が変わっても利用者の条件を黙って消さず、該当が戻れば再び効く）。
  const filter = useMemo<CandidateFilter>(
    () => ({
      teamAddress: teamAddresses.includes(teamAddress) ? teamAddress : "",
      minDurationHours: parseMinDurationHours(minDurationText),
      excludeRecurring,
    }),
    [excludeRecurring, minDurationText, teamAddress, teamAddresses],
  );
  const filteredEvents = useMemo(() => filterCandidates(events, filter), [events, filter]);
  const hiddenCount = events.length - filteredEvents.length;
  const filterActive = isFilterActive(filter);
  const resetFilter = () => {
    setTeamAddress("");
    setMinDurationText("");
    setExcludeRecurring(false);
  };

  // 選択中の予定が絞り込みで隠れたら、右パネルからも外して登録できないようにする。
  // 選択自体は保持し、絞り込みを戻せばそのまま続きから登録できる。
  const selectedCandidate = events.find((event) => event.id === selectedEventId);
  const selectedHidden =
    selectedCandidate !== undefined && candidateHiddenReason(selectedCandidate, filter) !== undefined;
  const selectedEvent = selectedHidden ? undefined : selectedCandidate;
  const selectedDates = selectedEvent ? candidateDates(selectedEvent) : [];

  const selectEvent = (event: OutlookCandidate) => {
    setSelectedEventId(event.id);
    setParticipantsByDate(Object.fromEntries(
      candidateDates(event).map((date) => [date, [...event.suggestedMemberIds]]),
    ));
    setNotice(null);
  };

  const loadEvents = async (formEvent?: FormEvent) => {
    formEvent?.preventDefault();
    if (!memberId || from > to) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    setSelectedEventId("");
    setParticipantsByDate({});
    try {
      setEvents(await getOutlookCandidates(memberId, from, to));
    } catch (loadError) {
      setEvents([]);
      setError(loadError instanceof Error ? loadError.message : "Outlook予定を取得できませんでした");
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (date: string, id: string, checked: boolean) => {
    setParticipantsByDate((current) => {
      const dateParticipants = current[date] ?? [];
      return {
        ...current,
        [date]: checked
          ? [...new Set([...dateParticipants, id])]
          : dateParticipants.filter((value) => value !== id),
      };
    });
  };

  const copyFirstDateParticipants = () => {
    const firstParticipants = participantsByDate[selectedDates[0] ?? ""] ?? [];
    setParticipantsByDate(Object.fromEntries(
      selectedDates.map((date) => [date, [...firstParticipants]]),
    ));
  };

  const saveEvent = async () => {
    if (
      !selectedEvent ||
      selectedDates.length === 0 ||
      selectedDates.some((date) => (participantsByDate[date] ?? []).length === 0)
    ) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await importOutlookEvent({
        memberId,
        eventId: selectedEvent.id,
        participantsByDate,
      });
      const details = [
        result.createdCount > 0 ? `${result.createdCount}件を登録` : "",
        result.updatedCount > 0 ? `${result.updatedCount}件を更新` : "",
      ].filter(Boolean).join("、");
      setEvents((current) => current.filter((event) => event.id !== selectedEvent.id));
      setSelectedEventId("");
      setParticipantsByDate({});
      setNotice(`Notionへ${details}しました。登録済みの予定を候補から除外しました`);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Notionへ登録できませんでした");
    } finally {
      setSaving(false);
    }
  };

  const combinedError = membersError || error;

  return (
    <main className="wrap">
      <div className="page-heading">
        <div>
          <h1>Outlook予定をNotionへ登録<span className="badge">選択モード</span></h1>
          <p className="sub">必要な予定だけ選択し、タイムラインへ表示する参加者を確定します。</p>
        </div>
        <a className="nav-link" href={`/${window.location.hash}`}>タイムラインへ戻る</a>
      </div>

      <form className="controls" onSubmit={(event) => void loadEvents(event)}>
        <div className="ctl">
          <label htmlFor="outlook-member">Outlookカレンダー</label>
          <select id="outlook-member" value={memberId} onChange={(event) => setMemberId(event.target.value)}>
            {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        </div>
        <div className="ctl">
          <label htmlFor="outlook-from">開始</label>
          <input id="outlook-from" type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
        </div>
        <div className="ctl">
          <label htmlFor="outlook-to">終了</label>
          <input id="outlook-to" type="date" value={to} min={from} onChange={(event) => setTo(event.target.value)} />
        </div>
        <button type="submit" disabled={!memberId || loading || membersLoading}>
          {loading ? "検索中…" : "予定を検索"}
        </button>
      </form>

      {events.length > 0 && (
        <div className="controls import-filters" role="group" aria-label="候補の絞り込み">
          <span className="ctl-label">絞り込み</span>
          {teamAddresses.length > 0 && (
            <div className="ctl">
              <label htmlFor="team-filter">チームアドレス</label>
              <select
                id="team-filter"
                value={filter.teamAddress}
                onChange={(event) => setTeamAddress(event.target.value)}
              >
                <option value="">すべて</option>
                {teamAddresses.map((address) => (
                  <option key={address} value={address}>{address.split("@")[0]}</option>
                ))}
              </select>
            </div>
          )}
          <div className="ctl">
            <label htmlFor="min-duration-filter">最小時間 (h)</label>
            <input
              id="min-duration-filter"
              type="number"
              min={0}
              max={24}
              step={0.5}
              placeholder="0"
              value={minDurationText}
              title="この時間未満の予定を候補から隠します。終日予定は隠しません"
              onChange={(event) => setMinDurationText(event.target.value)}
              onBlur={() => {
                // 確定時だけ 0〜24 に丸めた値を書き戻す。0 は空欄（絞り込まない）にする。
                const hours = parseMinDurationHours(minDurationText);
                setMinDurationText(hours > 0 ? String(hours) : "");
              }}
            />
          </div>
          <label className="chk">
            <input
              type="checkbox"
              checked={excludeRecurring}
              onChange={(event) => setExcludeRecurring(event.target.checked)}
            />
            定例（繰り返し）予定を隠す
          </label>
          <span className="filter-summary" role="status">
            {filterActive
              ? `${events.length}件中${filteredEvents.length}件を表示（${hiddenCount}件を絞り込みで非表示）`
              : `${events.length}件を表示`}
          </span>
          {filterActive && (
            <button type="button" className="link-button" onClick={resetFilter}>
              絞り込みを解除
            </button>
          )}
        </div>
      )}

      {combinedError && (
        <div className="status error" role="alert">
          <span>{combinedError}</span>
          {combinedError === AUTH_REQUIRED_MESSAGE && (
            <button type="button" disabled={signingIn} onClick={onSignIn}>
              {signingIn ? "サインイン中…" : "Microsoft 365でサインイン"}
            </button>
          )}
        </div>
      )}
      {notice && <div className="status success" role="status">{notice}</div>}

      <div className="import-layout">
        <section className="import-panel" aria-label="Outlook予定候補">
          <h2>1. 登録する予定を選択</h2>
          {loading && events.length === 0 ? (
            <p className="empty">検索中…</p>
          ) : events.length === 0 ? (
            <p className="empty">カレンダーと期間を指定して「予定を検索」を押してください。</p>
          ) : filteredEvents.length === 0 ? (
            <p className="empty">
              {hiddenCount > 0
                ? "絞り込み条件に一致する予定はありません。条件を緩めるか「絞り込みを解除」を押してください。"
                : "条件に一致する公開可能な予定はありません。"}
            </p>
          ) : (
            <div className="candidate-list">
              {filteredEvents.map((event) => (
                <button
                  type="button"
                  className={`candidate${event.id === selectedEventId ? " selected" : ""}`}
                  key={event.id}
                  onClick={() => selectEvent(event)}
                >
                  <span className="candidate-title">{event.title}</span>
                  <span className="candidate-time">{eventTime(event)}</span>
                  {(event.recurring || event.teamAddresses.length > 0) && (
                    <span className="candidate-tags">
                      {event.recurring && <span className="tag-recurring">定例</span>}
                      {event.teamAddresses.map((address) => <span key={address}>{address.split("@")[0]}</span>)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="import-panel" aria-label="参加者選択">
          <h2>2. タイムラインの参加者を確定</h2>
          {!selectedEvent ? (
            <p className="empty">
              {selectedHidden && selectedCandidate
                ? `選択していた「${selectedCandidate.title}」は絞り込みで非表示になっています。絞り込みを解除するか、左側から別の予定を選択してください。`
                : "左側から予定を選択してください。"}
            </p>
          ) : (
            <>
              <div className="selected-summary">
                <strong>{selectedEvent.title}</strong>
                <span>{eventTime(selectedEvent)}</span>
              </div>
              {selectedDates.length > 1 && (
                <button type="button" className="copy-participants" onClick={copyFirstDateParticipants}>
                  初日の参加者を全日にコピー
                </button>
              )}
              <div className="date-participant-list">
                {selectedDates.map((date) => (
                  <section className="date-participants" key={date}>
                    <h3>{date}<span>{(participantsByDate[date] ?? []).length}名</span></h3>
                    <div className="participant-grid">
                      {members.map((member) => (
                        <label className="participant" key={member.id}>
                          <input
                            type="checkbox"
                            checked={(participantsByDate[date] ?? []).includes(member.id)}
                            onChange={(event) => toggleParticipant(date, member.id, event.target.checked)}
                          />
                          {member.name}
                        </label>
                      ))}
                    </div>
                  </section>
                ))}
              </div>
              <button
                type="button"
                className="primary-action"
                disabled={
                  saving ||
                  selectedDates.some((date) => (participantsByDate[date] ?? []).length === 0)
                }
                onClick={() => void saveEvent()}
              >
                {saving ? "登録中…" : `Notionへ日別登録・更新（${selectedDates.length}日分）`}
              </button>
            </>
          )}
        </section>
      </div>

      <p className="footer">
        非公開・キャンセル済み予定は候補に表示しません。登録時にOutlookからタイトルと日時を再取得し、
        同じ予定は「Outlook Event ID」で上書き更新されます。
      </p>
    </main>
  );
}
